import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { vaultAPI, creditsAPI, attributionAPI, clientsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Lock, Key, CheckCircle, Warning, CircleNotch, Eye, EyeSlash, Plug, Copy, Lightning, Shield,
  ArrowRight, Globe, Robot, MagnifyingGlass, Megaphone, VideoCamera, Camera
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

const STATUS_DOT = { connected: 'var(--success)', active: 'var(--success)', live: 'var(--success)', not_connected: 'var(--text-tertiary)', waiting: 'var(--warning)', error: 'var(--error)', pending_review: 'var(--warning)', saved: 'var(--accent)' };
const CONN_ICONS = { google_search_console: MagnifyingGlass, tavily: Lightning, server_logs: Robot, dataforseo: Globe, meta_ads: Megaphone, google_ads_api: Megaphone, youtube_analytics: VideoCamera, linkedin_creator: Globe };

const CREATOR_CONNECTIONS = ['youtube_analytics', 'linkedin_creator'];

// Day 1 external-integrations audit (2026-09-22): most of these Connection
// Centre cards call `/connections/{provider}/test` routes that fabricate
// their "connected" preview via `random.*` (server.py, each route's own
// docstring). There is no real OAuth implementation anywhere in this
// backend for ANY provider -- every real connection in this product is a
// manually-pasted, encrypted API credential (the vault), never a redirect
// flow. Three of these five have no real backend connector at all
// (google_search_console/youtube_analytics/linkedin_creator) and are
// rendered as honest "Coming soon" cards, matching the existing Instagram
// card below. The other two (meta_ads/google_ads_api) DO have a real,
// working connector (connectors/paid_media.py, connectors/google_ads.py)
// reachable only from the Data Connections page under a different vault
// key name (meta_marketing/google_ads) -- this card now points there
// instead of offering a "Connect" action that can never do anything.
//
// SEO+Gemini provider activation: dataforseo is the one exception to the
// "fabricates via random.*" statement above -- its /test route now makes a
// real DataForSeoConnector.health_check() call once a real DATAFORSEO_API_KEY
// is configured (server.py). See the removed dataforseo-specific mock-notice
// branch further down for the corresponding UI-side fix.
const COMING_SOON_IDS = ['google_search_console', 'youtube_analytics', 'linkedin_creator'];
const REDIRECT_TO_DATA_CONNECTIONS = { meta_ads: 'Meta Ads', google_ads_api: 'Google Ads' };
// tavily/dataforseo are INFRASTRUCTURE_KEYS (server.py) -- get_vault_key()
// always resolves them from the shared master environment variable,
// never from a per-tenant vault entry, so a tenant's own "Save" here can
// never be read back. `/vault/save` correctly rejects both key names
// (not in CLIENT_CONNECTION_KEYS) -- rather than let a design partner hit
// that 400 every time, the input is replaced with an honest note.
const INFRA_MANAGED_IDS = ['tavily', 'dataforseo'];

export default function VaultPage() {
  const { user } = useAuth();
  const tenantType = user?.tenant_type || 'brand';
  const isCreator = tenantType === 'creator';
  const [health, setHealth] = useState(null);
  const [credits, setCredits] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [attribution, setAttribution] = useState(null);
  const [activeSnippet, setActiveSnippet] = useState('nginx');
  const [tavilyStatus, setTavilyStatus] = useState(null);
  const [dfStatus, setDfStatus] = useState(null);
  const [testing, setTesting] = useState('');
  const [keyInputs, setKeyInputs] = useState({});
  const [showKey, setShowKey] = useState({});
  const [testResults, setTestResults] = useState({});

  const loadAll = () => {
    axios.get(`${API}/connections/health`, auth()).then(r => setHealth(r.data)).catch(() => {});
    creditsAPI.usage().then(r => setCredits(r.data)).catch(() => {});
    axios.get(`${API}/connections/tavily/status`, auth()).then(r => setTavilyStatus(r.data)).catch(() => {});
    axios.get(`${API}/connections/dataforseo/status`, auth()).then(r => setDfStatus(r.data)).catch(() => {});
    clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSelectedClient(r.data[0].id); });
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (selectedClient) attributionAPI.setup(selectedClient).then(r => setAttribution(r.data)).catch(() => {}); }, [selectedClient]);

  const saveKey = async (keyName) => {
    if (!keyInputs[keyName]?.trim()) return;
    setTesting(keyName);
    try {
      await vaultAPI.save({ key_name: keyName, key_value: keyInputs[keyName] });
      toast.success(`${keyName} saved securely`);
      setKeyInputs(prev => ({ ...prev, [keyName]: '' }));
      loadAll();
      // Auto-run connection test after save
      setTimeout(() => testConnection(keyName), 500);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); setTesting(''); }
  };

  const testConnection = async (connId) => {
    setTesting(connId);
    try {
      const endpoints = {
        google_search_console: `${API}/connections/gsc/test`,
        tavily: `${API}/connections/tavily/test-som`,
        server_logs: `${API}/connections/logs/test-ping`,
        dataforseo: `${API}/connections/dataforseo/test`,
        meta_ads: `${API}/connections/meta/test`,
        google_ads_api: `${API}/connections/google-ads/test`,
        youtube_analytics: `${API}/connections/youtube/test`,
      };
      const res = await axios.post(endpoints[connId], {}, auth());
      setTestResults(prev => ({ ...prev, [connId]: res.data }));
      toast.success(`${connId}: ${res.data.status || 'tested'}`);
      loadAll();
    } catch (err) {
      setTestResults(prev => ({ ...prev, [connId]: { status: 'error', message: err.response?.data?.detail || 'Connection test failed. Check credentials and try again.' } }));
      toast.error(err.response?.data?.detail || 'Test failed');
    }
    finally { setTesting(''); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="vault-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">
              <Lock size={28} className="inline mr-3 text-[var(--accent)]" weight="duotone" />
              Connection Centre
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">API vault, infrastructure credits & server log attribution</p>
          </div>
        </div>

        {/* ═══ CONNECTION HEALTH SUMMARY ═══ */}
        {health && (
          <div className="nx-metric-card rounded-md" data-testid="connection-health">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">Connection Health</h3>
              <span className="text-[10px] font-mono text-[var(--text-secondary)]">
                {isCreator
                  ? `${health.connections?.filter(c => CREATOR_CONNECTIONS.includes(c.id) && c.connected).length || 0}/${CREATOR_CONNECTIONS.length + 1} connected`
                  : `${health.connected}/${health.total} connected`}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {(isCreator ? health.connections?.filter(c => CREATOR_CONNECTIONS.includes(c.id)) : health.connections)?.map(c => {
                const Icon = CONN_ICONS[c.id] || Key;
                return (
                  <div key={c.id} className="p-2 rounded border border-[var(--border)] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_DOT[c.status] || 'var(--text-tertiary)' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] text-[var(--text-primary)] truncate">{c.label}</p>
                      <p className="text-[8px] text-[var(--text-tertiary)] truncate">{c.status.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Infrastructure Credits */}
        {credits && (
          <div className="nx-metric-card rounded-md">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2"><Shield size={14} className="text-[var(--accent)]" weight="duotone" /><span className="text-xs text-[var(--text-primary)]">Infrastructure Credits</span><span className="encrypted-badge">Provided by Martian</span></div>
              <span className="text-[10px] font-mono text-[var(--text-primary)]">{credits.credits_used} / {credits.credits_limit}</span>
            </div>
            <div className="w-full h-2.5 bg-[var(--border-light)] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(credits.usage_percent, 100)}%`, background: credits.usage_percent > 80 ? 'var(--error)' : credits.usage_percent > 50 ? 'var(--warning)' : 'var(--accent)' }} />
            </div>
          </div>
        )}

        <Tabs defaultValue="connections" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="connections" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">6 API Connections</TabsTrigger>
            <TabsTrigger value="attribution" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Log Attribution</TabsTrigger>
          </TabsList>

          {/* ═══ 6 CONNECTION CARDS ═══ */}
          <TabsContent value="connections" className="space-y-4">
            {(isCreator ? health?.connections?.filter(c => CREATOR_CONNECTIONS.includes(c.id)) : health?.connections)?.map(conn => {
              const Icon = CONN_ICONS[conn.id] || Key;
              const isConnected = conn.connected;

              // No real backend exists for these at all (no OAuth flow, no
              // connector) -- an honest "Coming soon" card, same treatment
              // as the Instagram card below, instead of a Test/Connect
              // button that can never do anything real.
              if (COMING_SOON_IDS.includes(conn.id)) {
                return (
                  <div key={conn.id} className="nx-metric-card rounded-md opacity-60" data-testid={`conn-${conn.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded flex items-center justify-center bg-[var(--neutral-bg)]">
                          <Icon size={20} className="text-[var(--text-tertiary)]" weight="duotone" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[var(--text-primary)] font-bold">{conn.label}</span>
                            <span className="px-2 py-0.5 text-[8px] font-mono bg-[var(--neutral-bg)] text-[var(--text-secondary)] rounded">Coming Soon</span>
                          </div>
                          <p className="text-[10px] text-[var(--text-secondary)]">{conn.sub_label}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-[9px] text-[var(--text-tertiary)]">Not yet available in this environment — no live connection exists for {conn.label} today.</p>
                    <div className="flex items-center gap-1 flex-wrap mt-2">
                      <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Will flow to:</span>
                      {conn.flows_to?.map(f => <span key={f} className="px-1.5 py-0.5 text-[7px] rounded bg-[var(--accent-light)] text-[var(--accent)] font-mono">{f}</span>)}
                    </div>
                  </div>
                );
              }

              // A real, working connector exists for these -- just under a
              // different vault key, reachable only from Data Connections.
              // Point there instead of offering a dead-end "Connect".
              if (REDIRECT_TO_DATA_CONNECTIONS[conn.id]) {
                return (
                  <div key={conn.id} className="nx-metric-card rounded-md" data-testid={`conn-${conn.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded flex items-center justify-center bg-[var(--neutral-bg)]">
                          <Icon size={20} className="text-[var(--text-tertiary)]" weight="duotone" />
                        </div>
                        <div>
                          <span className="text-sm text-[var(--text-primary)] font-bold">{conn.label}</span>
                          <p className="text-[10px] text-[var(--text-secondary)]">{conn.sub_label}</p>
                        </div>
                      </div>
                      <a href="/growth/data-connections" data-testid={`conn-${conn.id}-redirect`}
                        className="inline-flex items-center gap-1 text-[9px] font-medium text-[var(--accent)] hover:underline whitespace-nowrap">
                        Manage in Data Connections <ArrowRight size={10} />
                      </a>
                    </div>
                    <p className="text-[9px] text-[var(--text-tertiary)]">{REDIRECT_TO_DATA_CONNECTIONS[conn.id]} connects from the Data Connections page for your brand — this Connection Centre card does not manage it.</p>
                    <div className="flex items-center gap-1 flex-wrap mt-2">
                      <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Data flows to:</span>
                      {conn.flows_to?.map(f => <span key={f} className="px-1.5 py-0.5 text-[7px] rounded bg-[var(--accent-light)] text-[var(--accent)] font-mono">{f}</span>)}
                    </div>
                  </div>
                );
              }

              return (
                <div key={conn.id} className={`nx-metric-card rounded-md ${isConnected ? 'border-[var(--success-border)]' : ''}`} data-testid={`conn-${conn.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded flex items-center justify-center ${isConnected ? 'bg-[var(--success-bg)]' : 'bg-[var(--neutral-bg)]'}`}>
                        <Icon size={20} className={isConnected ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'} weight="duotone" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[var(--text-primary)] font-bold">{conn.label}</span>
                          <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOT[conn.status] }} />
                          <span className="text-[9px] font-mono text-[var(--text-tertiary)]">{conn.status.replace(/_/g, ' ')}</span>
                        </div>
                        <p className="text-[10px] text-[var(--text-secondary)]">{conn.sub_label}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => testConnection(conn.id)} disabled={testing === conn.id}
                        className={`text-[9px] h-7 ${isConnected ? 'bg-[var(--surface)] border border-[var(--success-border)] text-[var(--success)] hover:bg-[var(--success-bg)]' : 'bg-[var(--accent)] text-white'}`}>
                        {testing === conn.id ? <CircleNotch size={10} className="animate-spin mr-1" /> : <Lightning size={10} className="mr-1" />}
                        Test
                      </Button>
                    </div>
                  </div>

                  {/* Connection-specific inputs -- hidden for infra-managed
                      keys (tavily/dataforseo), which are always resolved
                      from Martian's own shared environment variable and
                      can never actually be overridden by a per-tenant save
                      (server.py's get_vault_key INFRASTRUCTURE_KEYS branch;
                      /vault/save also rejects these key names outright). */}
                  {INFRA_MANAGED_IDS.includes(conn.id) ? (
                    <p className="text-[9px] text-[var(--text-tertiary)] mb-3">Managed centrally by Martian — no personal API key needed.</p>
                  ) : conn.type === 'api_key' ? (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="relative flex-1">
                        <Input type={showKey[conn.id] ? 'text' : 'password'} value={keyInputs[conn.id] || ''}
                          onChange={e => setKeyInputs(prev => ({ ...prev, [conn.id]: e.target.value }))}
                          placeholder={`Paste your ${conn.label} API key`}
                          className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] pr-8 font-mono text-[10px]" />
                        <button onClick={() => setShowKey(prev => ({ ...prev, [conn.id]: !prev[conn.id] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]">
                          {showKey[conn.id] ? <EyeSlash size={12} /> : <Eye size={12} />}
                        </button>
                      </div>
                      <Button size="sm" onClick={() => saveKey(conn.id)} disabled={!keyInputs[conn.id]?.trim()}
                        className="bg-[var(--accent)] text-white text-[9px] h-8 px-3">Save</Button>
                    </div>
                  ) : null}

                  {/* Tavily credit meter */}
                  {conn.id === 'tavily' && tavilyStatus && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-[9px] font-mono mb-1">
                        <span className="text-[var(--text-secondary)]">Credits: {tavilyStatus.credits_used} / {tavilyStatus.credits_limit}</span>
                        <span className={tavilyStatus.warning ? 'text-[var(--warning)]' : 'text-[var(--text-tertiary)]'}>~{tavilyStatus.days_remaining} days remaining</span>
                      </div>
                      <div className="w-full h-1.5 bg-[var(--border-light)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(tavilyStatus.credits_used / 1000) * 100}%`, background: tavilyStatus.warning ? 'var(--warning)' : 'var(--accent)' }} />
                      </div>
                    </div>
                  )}

                  {/* DataForSEO spend tracker */}
                  {conn.id === 'dataforseo' && dfStatus && (
                    <div className="mb-3 flex items-center gap-4 text-[9px] font-mono">
                      <span className="text-[var(--text-secondary)]">Today: ${dfStatus.today_spend}</span>
                      <span className="text-[var(--text-secondary)]">Month: ${dfStatus.monthly_spend}</span>
                      <span className={dfStatus.cap_reached ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}>Cap: ${dfStatus.daily_cap}/day</span>
                    </div>
                  )}

                  {/* SEO+Gemini provider activation: dataforseo's /test route
                      used to unconditionally fabricate its preview via
                      random.* (server.py), so this card always showed an
                      "illustrative preview" warning regardless of the real
                      response. The backend route now makes one real, minimal
                      DataForSeoConnector.health_check() call -- the warning
                      would now be actively FALSE for a genuine successful
                      connection, so the special case is removed; dataforseo
                      uses the same real success/error display as every other
                      connection below. */}
                  {testResults[conn.id] && (
                    <div className={`p-2.5 rounded border mt-3 ${testResults[conn.id].status === 'connected' || testResults[conn.id].status === 'ok' ? 'border-[var(--success-border)] bg-[var(--success-bg)]' : 'border-[var(--error-border)] bg-[var(--error-bg)]'}`}>
                      {(testResults[conn.id].status === 'connected' || testResults[conn.id].status === 'ok') ? (
                        <div className="flex items-start gap-2">
                          <CheckCircle size={14} className="text-[var(--success)] mt-0.5 flex-shrink-0" weight="fill" />
                          <div>
                            <p className="text-[10px] text-[var(--success)] font-medium">Connected successfully</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <Warning size={14} className="text-[var(--error)] mt-0.5 flex-shrink-0" weight="fill" />
                          <div>
                            <p className="text-[10px] text-[var(--error)] font-medium">{testResults[conn.id].message ? 'Not connected' : 'Connection test failed'}</p>
                            <p className="text-[9px] text-[var(--text-secondary)] mt-0.5">{testResults[conn.id].message || 'Unknown error'}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Data flows to */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Data flows to:</span>
                    {conn.flows_to?.map(f => <span key={f} className="px-1.5 py-0.5 text-[7px] rounded bg-[var(--accent-light)] text-[var(--accent)] font-mono">{f}</span>)}
                  </div>

                  {conn.last_sync && <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-1">Last sync: {new Date(conn.last_sync).toLocaleString()}</p>}
                </div>
              );
            })}

            {/* Instagram Coming Soon card — creator only */}
            {isCreator && (
              <div className="nx-metric-card rounded-md opacity-60" data-testid="conn-instagram">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded flex items-center justify-center bg-[var(--neutral-bg)]">
                      <Camera size={20} className="text-[var(--text-tertiary)]" weight="duotone" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[var(--text-primary)] font-bold">Instagram</span>
                        <span className="px-2 py-0.5 text-[8px] font-mono bg-[var(--neutral-bg)] text-[var(--text-secondary)] rounded">Coming Soon</span>
                      </div>
                      <p className="text-[10px] text-[var(--text-secondary)]">Reels affiliate performance and content citations</p>
                    </div>
                  </div>
                </div>
                <p className="text-[9px] text-[var(--text-tertiary)]">Instagram affiliate connection will be available when Meta Commerce API access is approved.</p>
                <div className="flex items-center gap-1 flex-wrap mt-2">
                  <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Data flows to:</span>
                  {['Creator Commerce Hub', 'Discovery-to-Revenue / Instagram'].map(f => <span key={f} className="px-1.5 py-0.5 text-[7px] rounded bg-[var(--accent-light)] text-[var(--accent)] font-mono">{f}</span>)}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ LOG ATTRIBUTION SETUP ═══ */}
          <TabsContent value="attribution" className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs text-[var(--text-tertiary)]">Client workspace:</span>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                  {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {attribution && (
              <>
                <div className="nx-metric-card rounded-md">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3"><Plug size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" />Webhook Ingestion URL</h3>
                  <div className="flex items-center gap-2 p-3 bg-[var(--surface-sunken)] rounded font-mono text-[10px] text-[var(--success)] overflow-x-auto">
                    <span className="flex-1 break-all">{attribution.webhook_url}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(attribution.webhook_url); toast.success('Copied'); }} className="p-1.5 rounded hover:bg-[var(--neutral-bg)] text-[var(--text-secondary)] flex-shrink-0"><Copy size={14} /></button>
                  </div>
                  <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-2">Tracked: {attribution.tracked_bots?.join(' | ')}</p>
                </div>
                <div className="nx-metric-card rounded-md">
                  <h3 className="text-sm font-bold text-[var(--text-primary)] mb-3">Server Config Snippets</h3>
                  <div className="flex items-center gap-2 mb-3">
                    {['nginx', 'apache', 'cloudflare'].map(s => (
                      <button key={s} onClick={() => setActiveSnippet(s)}
                        className={`px-3 py-1.5 text-[10px] font-mono uppercase rounded ${activeSnippet === s ? 'bg-[var(--accent)] text-white' : 'bg-[var(--neutral-bg)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'}`}>{s}</button>
                    ))}
                  </div>
                  <div className="relative">
                    <pre className="text-[10px] text-[var(--text-secondary)] bg-[var(--surface-sunken)] p-4 rounded overflow-x-auto max-h-40 font-mono leading-relaxed whitespace-pre-wrap">{attribution.snippets?.[activeSnippet] || ''}</pre>
                    <button onClick={() => { navigator.clipboard?.writeText(attribution.snippets?.[activeSnippet] || ''); toast.success('Snippet copied'); }}
                      className="absolute top-2 right-2 p-1.5 rounded bg-[var(--neutral-bg)] hover:bg-[var(--border)] text-[var(--text-secondary)]"><Copy size={12} /></button>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
